// Europe/Berlin timezone-safe date helpers for date-range quick presets.
// No date library is used: the only timezone-sensitive step is anchoring
// "today"/"now" to Europe/Berlin via Intl; once anchored, all further
// calendar arithmetic (add days, month/year boundaries) is done in UTC and
// has no DST/offset involvement.

import { getActiveIntlLocale } from '../i18n/intlLocale';

const BERLIN_TZ = 'Europe/Berlin';
const pad = (n, len = 2) => String(n).padStart(len, '0');

function getBerlinParts(instant) {
  // DO NOT localise this 'en-US'. Intl is used here as a PARSER, not a
  // formatter: formatToParts() below reads the numeric fields back out, and
  // every preset calculation (getPresetRange & friends) depends on them. A
  // locale with different numbering or calendar conventions would silently
  // corrupt every date range in the app. This is a machine format that happens
  // to go through a display API — it is not user-facing text.
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: BERLIN_TZ,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = Object.fromEntries(dtf.formatToParts(instant).map((p) => [p.type, p.value]));
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    // Intl with hour12:false can format midnight as "24" in some engines
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
    second: Number(parts.second),
  };
}

// Berlin's UTC offset in minutes at a given real instant (e.g. 120 during CEST, 60 during CET).
function getBerlinOffsetMinutesAt(instant) {
  const p = getBerlinParts(instant);
  const asIfUTC = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return Math.round((asIfUTC - instant.getTime()) / 60000);
}

function formatOffset(offsetMin) {
  const sign = offsetMin >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMin);
  return `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}

// Converts a Berlin *local* wall-clock date/time into a correct, DST-aware
// ISO 8601 string with explicit UTC offset (e.g. "2026-07-06T00:00:00+02:00").
export function berlinLocalToISOString(year, month, day, hour = 0, minute = 0, second = 0) {
  const guess = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  // DST transitions in Europe happen at 01:00/02:00 UTC (02:00/03:00 local),
  // so a midnight or end-of-day target never straddles the transition instant
  // itself — using the offset at the naive guess instant is safe here.
  const offsetMin = getBerlinOffsetMinutesAt(guess);
  return `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}:${pad(second)}${formatOffset(offsetMin)}`;
}

function toUTCDateOnly({ year, month, day }) {
  return new Date(Date.UTC(year, month - 1, day));
}
export function addDaysToDateOnly(d, n) {
  const c = new Date(d.getTime());
  c.setUTCDate(c.getUTCDate() + n);
  return c;
}
function isoWeekday(d) {
  const wd = d.getUTCDay();
  return wd === 0 ? 7 : wd; // 1=Mon..7=Sun
}
function mondayOfDateOnly(d) {
  return addDaysToDateOnly(d, -(isoWeekday(d) - 1));
}
function dateOnlyParts(d) {
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
}

// Formats a date-only UTC-midnight Date (as produced by getBerlinToday/addDaysToDateOnly) as "YYYY-MM-DD".
export function formatDateOnly(d) {
  const { year, month, day } = dateOnlyParts(d);
  return `${year}-${pad(month)}-${pad(day)}`;
}

export function getBerlinToday(now = new Date()) {
  const { year, month, day } = getBerlinParts(now);
  return toUTCDateOnly({ year, month, day });
}

function startOfDayISO(dateOnly) {
  const { year, month, day } = dateOnlyParts(dateOnly);
  return berlinLocalToISOString(year, month, day, 0, 0, 0);
}
function endOfDayISO(dateOnly) {
  const { year, month, day } = dateOnlyParts(dateOnly);
  return berlinLocalToISOString(year, month, day, 23, 59, 59);
}

// Calendar-aligned + rolling-window date range presets.
// "now"-anchored boundaries resolve to the end of the current Berlin calendar
// day, NOT the exact request instant — deliberately, so that every request
// (live or a backend warm pass, see shared/period_presets.py) made during the
// same Berlin day computes a byte-identical dateRange and therefore the same
// cache key. Before this, endDate used the exact current instant, which meant
// no two requests for the same rolling window ever shared a cache key (see
// git history / shared/period_presets.py's module docstring for the full
// rationale). This is safe: the OCEL file is a static, already-extracted
// snapshot, so it can never contain events after its own extraction moment
// regardless of how far into "today" the nominal end boundary reaches.
export function getPresetRange(presetKey, now = new Date()) {
  const today = getBerlinToday(now);
  const thisMonday = mondayOfDateOnly(today);
  const endOfTodayISO = endOfDayISO(today);

  switch (presetKey) {
    case 'today':
      return { startDate: startOfDayISO(today), endDate: endOfTodayISO };
    case 'yesterday': {
      const y = addDaysToDateOnly(today, -1);
      return { startDate: startOfDayISO(y), endDate: endOfDayISO(y) };
    }
    case 'thisWeek':
      return { startDate: startOfDayISO(thisMonday), endDate: endOfTodayISO };
    case 'lastWeek': {
      const lastMonday = addDaysToDateOnly(thisMonday, -7);
      return { startDate: startOfDayISO(lastMonday), endDate: endOfDayISO(addDaysToDateOnly(lastMonday, 6)) };
    }
    case 'last2Weeks':
      return { startDate: startOfDayISO(addDaysToDateOnly(thisMonday, -7)), endDate: endOfTodayISO };
    case 'thisMonth': {
      const { year, month } = getBerlinParts(now);
      return { startDate: berlinLocalToISOString(year, month, 1, 0, 0, 0), endDate: endOfTodayISO };
    }
    case 'lastMonth': {
      const { year, month } = getBerlinParts(now);
      const lastPrev = addDaysToDateOnly(toUTCDateOnly({ year, month, day: 1 }), -1);
      const { year: py, month: pm } = dateOnlyParts(lastPrev);
      return { startDate: berlinLocalToISOString(py, pm, 1, 0, 0, 0), endDate: endOfDayISO(lastPrev) };
    }
    case 'thisYear': {
      const { year } = getBerlinParts(now);
      return { startDate: berlinLocalToISOString(year, 1, 1, 0, 0, 0), endDate: endOfTodayISO };
    }
    case 'lastYear': {
      const { year } = getBerlinParts(now);
      return {
        startDate: berlinLocalToISOString(year - 1, 1, 1, 0, 0, 0),
        endDate: berlinLocalToISOString(year - 1, 12, 31, 23, 59, 59),
      };
    }
    // Previous-period targets for the "Letzter Monat"/"Letztes Jahr" presets
    // (Aggregation KPI's Vorperioden-Vergleich) — the calendar period two
    // steps back from "now", not selectable as a Betrachtungszeitraum itself.
    case 'monthBeforeLast': {
      const { year, month } = getBerlinParts(now);
      const lastPrev = addDaysToDateOnly(toUTCDateOnly({ year, month, day: 1 }), -1);
      const { year: py, month: pm } = dateOnlyParts(lastPrev);
      const secondPrevLast = addDaysToDateOnly(toUTCDateOnly({ year: py, month: pm, day: 1 }), -1);
      const { year: ppy, month: ppm } = dateOnlyParts(secondPrevLast);
      return { startDate: berlinLocalToISOString(ppy, ppm, 1, 0, 0, 0), endDate: endOfDayISO(secondPrevLast) };
    }
    case 'yearBeforeLast': {
      const { year } = getBerlinParts(now);
      return {
        startDate: berlinLocalToISOString(year - 2, 1, 1, 0, 0, 0),
        endDate: berlinLocalToISOString(year - 2, 12, 31, 23, 59, 59),
      };
    }
    // Rolling windows — fixed-length, start at Berlin midnight N days back, end = end of today
    case 'last7Days':
      return { startDate: startOfDayISO(addDaysToDateOnly(today, -6)), endDate: endOfTodayISO };
    case 'last14Days':
      return { startDate: startOfDayISO(addDaysToDateOnly(today, -13)), endDate: endOfTodayISO };
    case 'last30Days':
      return { startDate: startOfDayISO(addDaysToDateOnly(today, -29)), endDate: endOfTodayISO };
    case 'last90Days':
      return { startDate: startOfDayISO(addDaysToDateOnly(today, -89)), endDate: endOfTodayISO };
    case 'last12Months': {
      const { year, month, day } = getBerlinParts(now);
      return { startDate: berlinLocalToISOString(year - 1, month, day, 0, 0, 0), endDate: endOfTodayISO };
    }
    default:
      throw new Error(`Unknown preset: ${presetKey}`);
  }
}

// Formats a stored dateRange value (bare 'YYYY-MM-DD' or a full ISO-with-offset
// string) as a human-readable German day + time string in Europe/Berlin, for
// showing the user exactly what interval a preset (or manual edit) applied.
export function formatBerlinDisplay(raw) {
  if (!raw) return null;
  const instant = raw.includes('T') ? new Date(raw) : new Date(`${raw}T00:00:00Z`);
  if (Number.isNaN(instant.getTime())) return raw;
  // Unlike getBerlinParts above, this one IS user-facing, so the locale follows
  // the UI language. timeZone stays pinned to Berlin on purpose: the timezone
  // is a business fact (the OCEL data is Berlin-anchored), not a language
  // preference — an English-reading user in Chicago must still see Berlin wall
  // clock times, or the displayed timestamps stop matching the backend.
  return new Intl.DateTimeFormat(getActiveIntlLocale(), {
    timeZone: BERLIN_TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(instant);
}
